import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import {
  useDeleteManyVehicules,
  useGetAllVehicules,
  useUpdateManyVehicules,
} from '@/hooks/vehicule.hooks';
import { TVehiculeSchema } from '@/schemas/vehicule/vehicule.schema';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { PageInfoHeading } from '@/components/Header/Page/PageInfoHeading';
import { VehiculeTableView } from '../components/VehiculeTableView';
import { VehiculeTableHeaders } from '../components/VehiculeTableHeaders';
import { VehiculeTableData } from '../components/VehiculeTableData';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import Modal from '@/components/Modal';
import { VehiculeImportModal } from '@/components/Custom/Modal/VehiculeImportModal';
import { useDeactivateField } from '@/hooks/deactivate-field.hooks';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import { ImportIcon } from '@/components/SvgIcons/ImportIcon';
import { useUserRole } from '@/hooks/auth.hooks';
import { WarningMessage } from '@/components/Message/WarningMessage';

import { showModal } from '@/utils/modal.utils';

export const VehiculeListPage = () => {
  const { t } = useTranslation(['vehicule']);
  const navigate = useNavigate();
  const [totalActive, setTotalActive] = useState<number>(0);
  const { isAdmin, isGroupManager, isCompanyManager } = useUserRole();

  /**
   * === Deactivate Data section ===
   */
  const {
    numberOfItemsToDeactivate,
    pendingDeactivate,
    handleDeactivate,
    onConfirmDeactivate,
    action,
  } = useDeactivateField<TVehiculeSchema>({
    t,
    linkedFieldNames: ['vehiculeChecks'],
    useUpdateData: useUpdateManyVehicules,
  });
  /**
   * === END OF Deactivate Data section ===
   */

  /**
   * === Delete Data section ===
   */
  const {
    numberOfItemsToDeactivate: numberOfItemsToDelete,
    pendingDeactivate: pendingDelete,
    handleDeactivate: handleDelete,
    onConfirmDeactivate: onConfirmDelete,
  } = useDeactivateField<TVehiculeSchema>({
    t,
    actionType: 'delete',
    confirmModalId: '#delete-confirm-modal',
    linkedFieldNames: ['vehiculeChecks'],
    useDeleteData: useDeleteManyVehicules,
  });
  /**
   * === END OF Delete Data section ===
   */

  const onGetTotalActive = (total: number) => {
    setTotalActive(total);
  };

  const displayHeadButton = isAdmin || isGroupManager || isCompanyManager;

  return (
    <>
      <PageHeader
        title={t('common:vehicules')}
        totalItems={totalActive}
        headingElement={
          <PageInfoHeading text={t('common:detailsRelatedToCurrentSection')} />
        }
        actionListElement={
          displayHeadButton && (
            <>
              <HeadingButton
                text={t('common:importAList')}
                leftIcon={
                  <ImportIcon className="-mt-[1.5px] w-[15.5px] h-[17px] [&>path]:fill-light-inverse dark-mode:[&>path]:fill-gray-600" />
                }
                onClick={() => showModal('#vehicule-import-modal')}
              />
              <HeadingButton
                text={t('addVehicule')}
                leftIcon={
                  <i className="-mt-[1.5px] ki-outline ki-plus-circle !text-[19.4px] text-light-inverse dark-mode:text-gray-600" />
                }
                onClick={() => navigate('/vehicule/create')}
              />
            </>
          )
        }
      />
      <div>
        <VehiculeTableView<TVehiculeSchema>
          t={t}
          useGetAllData={useGetAllVehicules}
          onGetTotalActive={onGetTotalActive}
          tableHeaders={VehiculeTableHeaders}
          tableData={VehiculeTableData}
          pendingDeactivate={pendingDeactivate}
          onDeactivate={handleDeactivate}
          pendingDelete={pendingDelete}
          onDelete={handleDelete}
        />
      </div>
      <VehiculeImportModal id="vehicule-import-modal" t={t} />
      <Modal
        id="information-modal"
        title={t('common:information')}
        centerY
        showCancelBtn={false}
        showSecondBtn={true}
        secondBtnLabel="Ok"
        size="small"
        bodyClassName="py-4"
      >
        <WarningMessage
          message={
            action === 'delete'
              ? t('common:unableToDeleteLinkedItem')
              : t('common:unableToDeactivateLinkedItem')
          }
          containerClassName="max-w-none"
        />
      </Modal>
      <ConfirmationModal
        id="deactivation-confirm-modal"
        title={t('common:confirmation')}
        description={
          numberOfItemsToDeactivate > 1 ? (
            <Trans
              i18nKey="common:confirmDeactivateItems"
              components={{
                br: <br />,
                span: <span className="font-medium" />,
              }}
            />
          ) : (
            <Trans
              i18nKey="common:confirmDeactivateItem"
              components={{ br: <br /> }}
            />
          )
        }
        onConfirmLabel={t('common:deactivate')}
        onConfirm={onConfirmDeactivate}
      />
      <ConfirmationModal
        id="delete-confirm-modal"
        title={t('common:confirmation')}
        description={
          numberOfItemsToDelete > 1 ? (
            <Trans
              i18nKey="common:confirmDeleteItems"
              components={{
                br: <br />,
                span: <span className="font-medium" />,
              }}
            />
          ) : (
            t('common:confirmDeleteItem')
          )
        }
        onConfirmLabel={t('common:delete')}
        onConfirm={onConfirmDelete}
      />
    </>
  );
};
